/**
 * Client-side helpers for the recipe image upload flow (Steg 3b).
 *
 * Two concerns live here, both deliberately pure/injectable so the editor's
 * upload sequence is unit-testable without a real <canvas>/<Image> (jsdom
 * doesn't implement canvas decoding):
 *  - `validateImageFile`: reject (before any resize attempt) a file whose type
 *    isn't an allowed image MIME type, or that's over the size cap.
 *  - `resizeImageToJpeg`: the REAL canvas-based implementation — downscale to
 *    at most 1600px on the longest side and always re-encode as JPEG (~80%
 *    quality), regardless of the original type. Tests substitute a fast fake
 *    (see `RecipeEditorPage`'s `resizeImage` prop) instead of calling this.
 *  - `resizeImageForImport`: same canvas logic, but capped at a SMALLER
 *    1024px (`IMPORT_MAX_DIMENSION`) — the photo-import flow only needs
 *    enough resolution for the AI vision call to read the page, so a smaller
 *    upload cuts both latency and Gemini's cost. Used only by
 *    `PhotoImportButton`; the hero/step image path is unchanged (1600px).
 */

/** MIME types the API's presign endpoint accepts (see `ImageUploadRequest`). */
export const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/** Reject anything picked over this size, before even attempting a resize. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

/** The content type we always upload as — every image is re-encoded to JPEG. */
export const UPLOAD_CONTENT_TYPE = "image/jpeg";

/** Longest-side cap for the client-side resize (hero/step images). */
const MAX_DIMENSION = 1600;

/**
 * Longest-side cap for the photo-IMPORT resize specifically — smaller than
 * `MAX_DIMENSION` because the AI vision call only needs to read the page, not
 * render a hero image; a smaller upload cuts both latency and AI cost.
 */
const IMPORT_MAX_DIMENSION = 1024;

/** JPEG re-encode quality (0..1). */
const JPEG_QUALITY = 0.8;

/**
 * Validate a picked file BEFORE attempting any resize. Returns a short
 * Norwegian error message on failure, or `null` when the file is acceptable.
 */
export function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
    return "Filtypen støttes ikke. Bruk JPEG, PNG eller WebP.";
  }
  if (file.size > MAX_IMAGE_BYTES) {
    return "Bildet er for stort (maks 10 MB).";
  }
  return null;
}

/**
 * Resize + re-encode a picked image file to a JPEG blob, capped at
 * `MAX_DIMENSION` on the longest side. Real canvas/Image implementation —
 * substitute an injected fake in tests (jsdom cannot decode real image bytes).
 */
export async function resizeImageToJpeg(file: File): Promise<Blob> {
  return resizeImageToJpegCapped(file, MAX_DIMENSION);
}

/**
 * Same re-encode as `resizeImageToJpeg`, capped at the smaller
 * `IMPORT_MAX_DIMENSION` — see the file-level docs. Used by
 * `PhotoImportButton` only.
 */
export async function resizeImageForImport(file: File): Promise<Blob> {
  return resizeImageToJpegCapped(file, IMPORT_MAX_DIMENSION);
}

async function resizeImageToJpegCapped(file: File, maxDimension: number): Promise<Blob> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const { width, height } = scaledSize(
      img.naturalWidth || img.width,
      img.naturalHeight || img.height,
      maxDimension,
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Kunne ikke behandle bildet.");
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), UPLOAD_CONTENT_TYPE, JPEG_QUALITY);
    });
    if (!blob) throw new Error("Kunne ikke behandle bildet.");
    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Kunne ikke lese bildet."));
    img.src = src;
  });
}

function scaledSize(
  width: number,
  height: number,
  maxDimension: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDimension || longest === 0) return { width, height };
  const scale = maxDimension / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}
